import type { Restaurant, RestaurantUpdateBody } from '@restaurant/api-client';
import { RestaurantUpdateSchema } from '@restaurant/api-contracts';
import { isValidContactPhone } from './contact-phone';
import { socialLinkDisplay, socialLinkToSave, type SocialLinkField } from './social-links';

export const PROFILE_FIELDS = [
  'name',
  'address',
  'postalCode',
  'city',
  'country',
  'contactEmail',
  'contactPhone',
  'facebookUrl',
  'instagramUrl',
  'tiktokUrl',
  'websiteUrl',
] as const;

export type ProfileValues = Record<(typeof PROFILE_FIELDS)[number], string>;

export const EMPTY_PROFILE: ProfileValues = {
  name: '',
  address: '',
  postalCode: '',
  city: '',
  country: 'DE',
  contactEmail: '',
  contactPhone: '',
  facebookUrl: '',
  instagramUrl: '',
  tiktokUrl: '',
  websiteUrl: '',
};

export function profileValues(restaurant: Restaurant): ProfileValues {
  return {
    name: restaurant.name,
    address: restaurant.address ?? '',
    postalCode: restaurant.postalCode ?? '',
    city: restaurant.city ?? '',
    country: restaurant.country,
    contactEmail: restaurant.contactEmail ?? '',
    contactPhone: restaurant.contactPhone ?? '',
    facebookUrl: socialLinkDisplay('facebookUrl', restaurant.facebookUrl),
    instagramUrl: socialLinkDisplay('instagramUrl', restaurant.instagramUrl),
    tiktokUrl: socialLinkDisplay('tiktokUrl', restaurant.tiktokUrl),
    websiteUrl: socialLinkDisplay('websiteUrl', restaurant.websiteUrl),
  };
}

export function profileBody(
  values: ProfileValues,
  saved: ProfileValues,
  links: Pick<Restaurant, SocialLinkField> | undefined,
): RestaurantUpdateBody {
  const changed = (field: keyof ProfileValues) => values[field].trim() !== saved[field].trim();
  const text = (field: keyof ProfileValues) => values[field].trim();
  const link = (field: SocialLinkField) => socialLinkToSave(field, values[field], links?.[field]);
  return {
    ...(changed('name') && { name: text('name') }),
    ...(changed('address') && { address: text('address') }),
    ...(changed('postalCode') && { postalCode: text('postalCode') }),
    ...(changed('city') && { city: text('city') }),
    ...((changed('country') || changed('contactPhone')) && { country: values.country }),
    ...(changed('contactEmail') && { contactEmail: text('contactEmail') || null }),
    ...(changed('contactPhone') && { contactPhone: text('contactPhone') || null }),
    ...(changed('facebookUrl') && { facebookUrl: link('facebookUrl') }),
    ...(changed('instagramUrl') && { instagramUrl: link('instagramUrl') }),
    ...(changed('tiktokUrl') && { tiktokUrl: link('tiktokUrl') }),
    ...(changed('websiteUrl') && { websiteUrl: link('websiteUrl') }),
  };
}

export function profileSchema(phoneInvalid: string) {
  return RestaurantUpdateSchema.superRefine((body, ctx) => {
    if (
      body.contactPhone &&
      body.country &&
      !isValidContactPhone(body.contactPhone, body.country)
    ) {
      ctx.addIssue({ code: 'custom', path: ['contactPhone'], message: phoneInvalid });
    }
  });
}
